import { getAuth } from 'firebase/auth'
import * as Sentry from '@sentry/browser'
import { AnalysisRunSchema } from '../analysis/schemas'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { readResponseJson, getResponseDebugContext } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

/** Read progress or apply an explicit control; ordinary tables have no analysis. */
export async function getTableAnalysis(tableId: string, signal?: AbortSignal, action?: 'cancel' | 'run-updated', rowIds?: string[]) {
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error('Sign in to access tables.')
  const params = new URLSearchParams()
  for (const id of rowIds ?? []) params.append('row_ids', id)
  const url = `${kPaxtonAppApiRESTBaseUrl()}/api/v1/tables/${tableId}/analysis${action ? `/${action}` : ''}${!action && params.size ? `?${params}` : ''}`
  const response = await fetch(url, { method: action ? 'POST' : 'GET', headers: { Authorization: `Bearer ${token}` }, signal })
  if (response.status === 404 && !action) return null
  const { data, parseError } = await readResponseJson(response)
  signal?.throwIfAborted()
  if (!response.ok) {
    if (response.status >= 500) Sentry.captureException(new Error('Table analysis request failed'), { extra: getResponseDebugContext(response, url) })
    const detail = data && typeof data === 'object' && 'detail' in data ? data.detail : undefined
    const approvedMessages = [
      'Matter not found',
      'Table resource not found.',
      'Table creator access required',
      'Wait for the current analysis to finish or cancel it first.',
      'There are no new, changed, or unanswered analysis questions.',
    ]
    const fallback = action ? 'Unable to update analysis. Please retry.' : 'Unable to load analysis progress. Please retry.'
    throw new ApiError(
      response.status,
      response.status >= 400 && response.status < 500 && typeof detail === 'string' && approvedMessages.includes(detail) ? detail : fallback
    )
  }
  const parsed = AnalysisRunSchema.safeParse(data)
  if (parseError || !parsed.success) {
    Sentry.captureException(new Error('Invalid analysis response'), { extra: getResponseDebugContext(response, url) })
    throw new ApiResponsePayloadError(response.status, 'The analysis response could not be read. Please retry.')
  }
  return parsed.data
}
